/**
 * 経費申請の作成と再申請(5段階フローの入口)。
 * 遷移条件は firestore.rules と二重に実装する。
 *
 * - 支払日・支払方法は申請時に入力しない(承認後の支払いステップで入力)。
 *   payment_date は申請者が入力する支払予定日を入れ、支払い時に実日付へ更新する
 * - 支払先は自由記述(counterparty)・必須。マスタ選択に戻す場合は vendor_id を使う
 * - 管理番号(K-)は作成時に自動採番。請求書申請(I-)は廃止(既存データの閲覧・処理は継続)
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, type QueryDocumentSnapshot, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fiscalYearOf, toMonth } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import { nextExpenseCode } from '@core/lib/numbering';
import { buildNotifications, notifyUsers } from './notificationsRepo';
import { fetchFlowPatterns, findPattern } from './flowPatterns';
import { runFollowUps } from './flowResult';
import { fetchUsersFromServer } from '@core/masters/masterRepo';
import { actsAsManagerOf, flowRoleFromLegacy, type DateStr, type DepartmentId, type DocType, type Payer, type PaymentMethod, type StoredFile, type Transaction, type User } from '@core/lib/types';

const COLLECTION = 'transactions';

function toTransaction(snap: QueryDocumentSnapshot): Transaction {
  return { id: snap.id, ...(snap.data() as object) } as Transaction;
}

export function newApplicationId(): string {
  return doc(collection(getDb(), COLLECTION)).id;
}

export function roleOf(u: User) {
  return u.flow_role ?? flowRoleFromLegacy(u.role, u.is_accounting);
}

/**
 * 遷移の通知先(部門長=担当事業部のみ、承認者・経理=全員)。
 * 2026-09-08: 承認者も「部門長として担当する事業部」を持てる(その事業部の部門長確認が承認者に回る)
 */
export async function usersForNotification(kind: 'bucho' | 'approver' | 'accounting', departmentId?: DepartmentId): Promise<string[]> {
  return usersForNotificationFrom(await fetchUsersFromServer(), kind, departmentId);
}

/**
 * 取得済みの名簿から宛先を選ぶ(通信しない)。
 * 1回の操作で名簿を何度も取りに行かないための形(2026-09-30: 申請の送信が遅い件)
 */
export function usersForNotificationFrom(
  users: User[],
  kind: 'bucho' | 'approver' | 'accounting',
  departmentId?: DepartmentId,
): string[] {
  return users
    .filter((u) => u.active)
    .filter((u) => {
      // 部門長の数え方は事業部の部門長一覧(manager_user_ids)と共通(types.ts actsAsManagerOf)
      if (kind === 'bucho') return !!departmentId && actsAsManagerOf(u, departmentId);
      return roleOf(u) === kind;
    })
    .map((u) => u.id);
}

/** 部門長として動く人か(役割が部門長、または担当事業部を持つ承認者。2026-09-08) */
export function actsAsManager(u: Pick<User, 'flow_role' | 'role' | 'is_accounting' | 'manager_department_ids'>): boolean {
  const role = roleOf(u as User);
  return role === 'bucho' || (role === 'approver' && u.manager_department_ids.length > 0);
}

/** 管理番号(コード)の組み立てに使う科目マスタの項目 */
export type AccountForCode = {
  item_category?: string | null;
  code?: string | null;
  abbr?: string | null;
  name?: string | null;
};

export type ExpenseApplicationInput = {
  companyId: string;
  departmentId: DepartmentId;
  /** 拠点(階層マスタのノード)。任意 */
  hierarchyNodeId: string;
  /** 支払先(自由記述・必須)。マスタ選択(vendor_id)へ戻せるよう vendor_id は残置 */
  counterparty: string;
  accountId: string;
  /** 任意 */
  subAccountId: string;
  postingDate: DateStr;
  /** 支払予定日(申請者が入力・必須)。支払い時に実際の日付で上書きされる */
  paymentDate: DateStr;
  amount: number;
  payer: Exclude<Payer, null>;
  /** 支払い方法(§2のパターン表で支払者との組合せを検証)。立替型かどうかは支払方法で決まる */
  paymentMethod: PaymentMethod;
  description: string;
  /** 備考(任意・v3.7) */
  note: string;
};

function baseDoc(userId: string) {
  const now = serverTimestamp();
  return {
    tax_mode: 'inclusive',
    tax_amount: null,
    due_date: null,
    counterparty: null,
    payment_method: null,
    evidence_type: null,
    receipts: [],
    is_internal: false,
    counterparty_department_id: null,
    hierarchy_node_id: null,
    approval_rule_id: '',
    approval_snapshot: [],
    current_step: 0,
    approvals: [],
    applied_at: now,
    applied_by: userId,
    manager_checked_at: null,
    manager_checked_by: null,
    approved_at: null,
    approved_by: null,
    paid_at: null,
    paid_by: null,
    completed_at: null,
    completed_by: null,
    cancelled_at: null,
    cancelled_by: null,
    submitted_at: null,
    submitted_by: null,
    confirmed_at: null,
    confirmed_by: null,
    rejected_at: null,
    rejected_by: null,
    reject_reason: null,
    rejected_from_status: null,
    source: 'manual',
    cash_entry_id: null,
    recurring_cost_id: null,
    created_by: userId,
    created_at: now,
    updated_by: userId,
    updated_at: now,
  };
}

/**
 * 部門長・承認者・経理の申請は部門長確認を省略する(v1.1 §1)。
 * 承認は必ず承認者が行い、自分の申請を自分で承認することはできない
 */
export function skipsManagerCheck(applicant: User, _departmentId: DepartmentId): boolean {
  return ['bucho', 'approver', 'accounting'].includes(roleOf(applicant));
}

/** 部門長確認を省略するか(申請者が管理側のとき。事業部に部門長がいない場合は resolveStartStatus 側で省略) */
export function managerCheckSkipped(applicant: User, departmentId: DepartmentId): boolean {
  return skipsManagerCheck(applicant, departmentId);
}

/** 承認後(支払い待ち)の支払い者への通知。承認・各種スキップの両方から使う */
export async function notifyPayerAfterApproval(tx: {
  id: string;
  code: string | null;
  departmentId: DepartmentId;
  createdBy: string;
  payer: Payer;
  advance: boolean;
  paymentMethod?: PaymentMethod | null;
}): Promise<void> {
  if (tx.paymentMethod === 'prepaid') {
    // 仮払い: 次は経理の仮払い(v1.1 §3-C-4)
    await notifyUsers({
      userIds: await usersForNotification('accounting'),
      transactionId: tx.id,
      message: `${tx.code} の仮払いをお願いします`,
      kind: 'action',
      stage: 'pay',
    });
  } else if (tx.payer === 'self') {
    // 自分で支払う分は「支払い」ページの操作依頼として届く(2026-08-26 ページ分割)
    await notifyUsers({
      userIds: [tx.createdBy],
      transactionId: tx.id,
      message: `${tx.code} が承認されました。「支払い待ち」画面から支払いを登録してください`,
      kind: 'action',
      stage: 'pay',
    });
  } else if (tx.payer === 'bucho') {
    // 事業部に部門長がいない場合(承認者・経理本人の申請等)は、
    // もう一方の承認者が部門長役として支払う(p15・2026-08-26)
    const buchos = await usersForNotification('bucho', tx.departmentId);
    const payTargets =
      buchos.length > 0
        ? buchos
        : (await usersForNotification('approver')).filter((id) => id !== tx.createdBy);
    await notifyUsers({
      userIds: payTargets,
      transactionId: tx.id,
      message: `${tx.code} の支払いをお願いします${tx.advance ? '(部門長立替)' : '(部門長依頼)'}`,
      kind: 'action',
      stage: 'pay',
    });
  } else {
    await notifyUsers({
      userIds: await usersForNotification('accounting'),
      transactionId: tx.id,
      message: `${tx.code} の支払いをお願いします`,
      kind: 'action',
      stage: 'pay',
    });
  }
}

/** 申請直後の通知(開始ステータス別) */
async function notifyNextAfterApply(tx: {
  id: string;
  code: string;
  departmentId: DepartmentId;
  startStatus: 'applied' | 'manager_checked' | 'approved';
  createdBy: string;
  payer: Payer;
  advance: boolean;
  paymentMethod: PaymentMethod;
  /** 申請時に取得済みの名簿(再取得しない) */
  users: User[];
}) {
  if (tx.startStatus === 'approved') {
    // 承認者本人の申請で部門長不在 → 申請と同時に支払い待ち
    await notifyPayerAfterApproval(tx);
  } else if (tx.startStatus === 'manager_checked') {
    await notifyUsers({
      userIds: usersForNotificationFrom(tx.users, 'approver'),
      transactionId: tx.id,
      message: `${tx.code} の承認をお願いします(部門長確認は省略)`,
      kind: 'action',
      stage: 'approve',
    });
  } else {
    await notifyUsers({
      userIds: usersForNotificationFrom(tx.users, 'bucho', tx.departmentId),
      transactionId: tx.id,
      message: `${tx.code} の部門長確認をお願いします`,
      kind: 'action',
      stage: 'approve',
    });
  }
  // 進捗連絡(info通知)は廃止(2026-08-26 指示)。進捗は申請一覧の「状態」列で確認する
}

/**
 * 申請の開始ステータス(v1.1)。
 * 部門長・承認者・経理・「その他」事業部は部門長確認を省略して承認待ちへ。
 * 選んだ事業部に部門長がいない場合も省略する(2026-08-26 全事業部申請対応)。
 * 承認は必ず承認者が行う(自己承認は禁止のため、承認者本人の申請も承認待ちで止まる)
 */
function resolveStartStatus(
  users: User[],
  applicant: User,
  departmentId: DepartmentId,
): 'applied' | 'manager_checked' | 'approved' {
  if (managerCheckSkipped(applicant, departmentId)) return 'manager_checked';
  const buchos = usersForNotificationFrom(users, 'bucho', departmentId);
  return buchos.length === 0 ? 'manager_checked' : 'applied';
}

export async function createExpenseApplication(params: {
  id: string;
  input: ExpenseApplicationInput;
  attachments: StoredFile[];
  applicant: User;
  fiscalStartMonth: number;
  /**
   * 科目マスタの情報。画面が既に持っているものを渡す(渡さないと1往復ぶん通信が増える)。
   * 2026-09-30: 携帯回線で送信が遅い件の対策
   */
  account?: AccountForCode | null;
}): Promise<{ code: string; startStatus: 'applied' | 'manager_checked' | 'approved'; warning: string | null }> {
  const { id, input, attachments, applicant, fiscalStartMonth } = params;
  const userId = applicant.id;
  if (!isValidAmount(input.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!input.counterparty.trim()) throw new Error('支払先を入力してください');
  if (!input.accountId) throw new Error('科目①を選んでください');
  if (!input.paymentDate) throw new Error('支払予定日を入力してください');
  if (!input.paymentMethod) throw new Error('支払い方法を選んでください');
  if (!input.description.trim()) throw new Error('摘要は必須です');
  // 振込は申請時に請求書の添付が必須(v1.1 §4)
  if (input.paymentMethod === 'bank_transfer' && attachments.length === 0) {
    throw new Error('「振込」は請求書の添付が必須です');
  }
  // 送信にかかる時間は「通信の往復回数」でほぼ決まる(2026-09-30 指摘: 携帯回線で送信が遅い)。
  // 互いに関係のない取得は同時に始め、順番に待たない
  const patternsPromise = fetchFlowPatterns();
  const accountPromise: Promise<AccountForCode | null> =
    params.account !== undefined
      ? Promise.resolve(params.account)
      : getDoc(doc(getDb(), 'accounts', input.accountId)).then((s) =>
          s.exists() ? (s.data() as AccountForCode) : null,
        );
  // 名簿は「部門長がいるか」と「通知の宛先」の両方で使う。取得は1回だけ。
  // キャッシュの古い名簿で判断すると部門長確認を飛ばしてしまうため、必ずサーバーから取る
  const usersPromise = fetchUsersFromServer();

  // 役割×支払方法×支払者はパターン表(§2)の14組合せのみ有効
  const patterns = await patternsPromise;
  const pattern = findPattern(patterns, roleOf(applicant), input.paymentMethod, input.payer);
  if (!pattern) {
    throw new Error('この支払方法と支払い者の組合せは選べません(申請フロー仕様§2)');
  }

  // 管理番号(2026-08-26 新ルール): 会社-項目-科目略称-YYYY-MM-連番(会社×科目×年月ごと)
  const year = Number(input.postingDate.slice(0, 4));
  const month = Number(input.postingDate.slice(5, 7));
  const account = await accountPromise;
  // 採番(読み書き2往復)は重いので、名簿の取得と並行して進める
  const codePromise = nextExpenseCode({
    company: input.companyId.toUpperCase(),
    item: account?.item_category ?? '販管',
    // 略称=科目マスタの「コード」欄(Excel C列。2026-08-26 に略称欄を廃止しコードへ一本化)
    abbr: account?.code || account?.abbr || account?.name?.slice(0, 3) || 'その他',
    year,
    month,
  });
  const users = await usersPromise;
  const startStatus = resolveStartStatus(users, applicant, input.departmentId);
  const code = await codePromise;
  // 立替型かどうかは支払方法で決まる(advance=立替型フラグ)
  const advance = input.paymentMethod === 'reimbursement';

  /**
   * 次の担当者への通知は、申請と同じ1回の書き込みにまとめる(2026-09-30: 送信が遅い件)。
   * 通信の往復が1回減り、かつ「申請はできたのに通知が届かない」状態が起きなくなる。
   *
   * 権限ルールの式評価は1リクエスト1000式まで(超えると書き込みごと拒否される。
   * 2026-08-26・09-09・09-10 に同種の事故)。2026-09-30 に test API で実測したところ
   * 申請の作成=約284式 / 通知1件=約37式。宛先が増えると上限に近づくため、
   * 通知の書き込みが10件(=約654式)を超える場合は、今までどおり別の書き込みで送る
   */
  const MAX_NOTIFICATION_WRITES_IN_BATCH = 10;
  const notifications =
    startStatus === 'applied'
      ? buildNotifications({
          userIds: usersForNotificationFrom(users, 'bucho', input.departmentId),
          transactionId: id,
          message: `${code} の部門長確認をお願いします`,
          kind: 'action',
          stage: 'approve',
        })
      : startStatus === 'manager_checked'
        ? buildNotifications({
            userIds: usersForNotificationFrom(users, 'approver'),
            transactionId: id,
            message: `${code} の承認をお願いします(部門長確認は省略)`,
            kind: 'action',
            stage: 'approve',
          })
        : // 承認者本人の申請で部門長が不在のとき(まれ)は支払い者への通知になるため、従来どおり別に送る
          [];
  const batchedNotifications =
    notifications.length > 0 && notifications.length <= MAX_NOTIFICATION_WRITES_IN_BATCH;

  const batch = writeBatch(getDb());
  batch.set(doc(getDb(), COLLECTION, id), {
    ...baseDoc(userId),
    type: 'expense',
    status: startStatus,
    // 部門長確認の省略時は本人を記録(「その他」事業部・部門長本人)。承認者の部門長不在開始は未確認のまま
    manager_checked_at: startStatus === 'manager_checked' ? serverTimestamp() : null,
    manager_checked_by: startStatus === 'manager_checked' ? userId : null,
    approved_at: startStatus === 'approved' ? serverTimestamp() : null,
    approved_by: startStatus === 'approved' ? userId : null,
    code,
    doc_type: 'expense' satisfies DocType,
    payer: input.payer,
    pattern_id: pattern.id,
    advance,
    reimbursed_at: null,
    reimbursed_by: null,
    reimbursement_date: null,
    reimbursement_cash_entry_id: null,
    settlement_receipts: [],
    settlement_received_at: null,
    settlement_received_by: null,
    received_receipts: [],
    prepaid_cash_entry_id: null,
    // 支払先は自由記述(counterparty)。マスタ選択に戻す場合は vendor_id を使う
    vendor_id: null,
    counterparty: input.counterparty.trim() || null,
    hierarchy_node_id: input.hierarchyNodeId || null,
    invoice_no: null,
    invoice_date: null,
    company_id: input.companyId,
    department_id: input.departmentId,
    account_id: input.accountId,
    sub_account_id: input.subAccountId || '',
    amount: input.amount,
    applied_amount: input.amount,
    posting_date: input.postingDate,
    posting_month: toMonth(input.postingDate),
    // 申請時は支払予定日。支払い時に実際の支払日で上書きされる
    payment_date: input.paymentDate,
    payment_month: toMonth(input.paymentDate),
    fiscal_year: fiscalYearOf(input.postingDate, fiscalStartMonth),
    payment_method: input.paymentMethod,
    description: input.description.trim(),
    note: input.note.trim() || null,
    attachments,
  });
  if (batchedNotifications) {
    for (const w of notifications) batch.set(w.ref, w.data);
  }
  await batch.commit();

  // まとめられなかった分だけ、あとから送る。
  // 申請は保存済みなので、通知に失敗しても「申請に失敗」とは出さない
  // (出すと二重に申請されるため。2026-09-17 改修依頼 B)
  if (batchedNotifications) return { code, startStatus, warning: null };
  const { warning } = await runFollowUps([
    [
      '次の担当者への通知',
      () =>
        notifyNextAfterApply({
          id,
          code,
          departmentId: input.departmentId,
          startStatus,
          createdBy: userId,
          payer: input.payer,
          advance,
          paymentMethod: input.paymentMethod,
          users,
        }),
    ],
  ]);
  return { code, startStatus, warning };
}

export type ExpenseReportInput = {
  companyId: string;
  departmentId: DepartmentId;
  hierarchyNodeId: string;
  /** 支払先(自由記述・必須。2026-08-27 追加) */
  counterparty: string;
  accountId: string;
  subAccountId: string;
  postingDate: DateStr;
  /** 支払い日(既に支払い済みのため「予定」ではない)。クレカのときは引き落とし日 */
  paymentDate: DateStr;
  amount: number;
  paymentMethod: PaymentMethod;
  description: string;
  note: string;
  /** クレカのとき(2026-09-07): 使った会社のカード(口座マスタ)と購入日。CFへ引き落とし日の出金(予定)を記録する */
  paymentAccountId?: string | null;
  purchaseDate?: DateStr | null;
};

/**
 * 経費申請(承認不要)=事後報告(2026-08-26 会議・spec v1.2 §3-D)。
 * 支払い済みの経費をレシート添付で報告する。**報告時に実績計上**(status=paid で作成)。
 * その後は 経理確認 → 承認者確認(completed)の2確認のみ(事前承認なし)
 */
export async function createExpenseReport(params: {
  id: string;
  input: ExpenseReportInput;
  receipts: StoredFile[];
  applicant: User;
  fiscalStartMonth: number;
}): Promise<{ code: string; warning: string | null }> {
  const { id, input, receipts, applicant, fiscalStartMonth } = params;
  const userId = applicant.id;
  if (!isValidAmount(input.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!input.accountId) throw new Error('科目①を選んでください');
  if (!input.paymentDate) throw new Error('支払い日を入力してください');
  if (!input.counterparty.trim()) throw new Error('支払先を入力してください');
  if (!input.description.trim()) throw new Error('摘要は必須です');
  if (receipts.length === 0) throw new Error('レシート(領収書)の添付が必須です');

  const year = Number(input.postingDate.slice(0, 4));
  const month = Number(input.postingDate.slice(5, 7));
  const accountSnap = await getDoc(doc(getDb(), 'accounts', input.accountId));
  const account = accountSnap.exists()
    ? (accountSnap.data() as { item_category?: string; code?: string; abbr?: string; name?: string })
    : null;
  const code = await nextExpenseCode({
    company: input.companyId.toUpperCase(),
    item: account?.item_category ?? '販管',
    abbr: account?.code || account?.abbr || account?.name?.slice(0, 3) || 'その他',
    year,
    month,
  });

  // クレカ(2026-09-07): 会社のカードなら引き落とし日の出金(予定)をCFへ。申請と同じバッチで書く
  const db = getDb();
  const batch = writeBatch(db);
  let cashEntryId: string | null = null;
  if (input.paymentMethod === 'card' && input.paymentAccountId) {
    cashEntryId = doc(collection(db, 'cash_entries')).id;
    batch.set(doc(db, 'cash_entries', cashEntryId), {
      cash_account_id: input.paymentAccountId,
      date: input.paymentDate,
      direction: 'out',
      amount: input.amount,
      status: 'planned',
      description: input.description.trim() || code,
      counterparty: input.counterparty.trim() || null,
      transaction_id: id,
      import_batch_id: null,
      dedup_hash: null,
      matched_at: null,
      matched_by: null,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
  }

  batch.set(doc(db, COLLECTION, id), {
    ...baseDoc(userId),
    type: 'expense',
    doc_type: 'expense' satisfies DocType,
    status: 'paid',
    source: 'report',
    code,
    payer: 'self',
    pattern_id: null,
    // 立替の報告は経理の精算(まとめて精算)の対象にする(2026-09-04)
    advance: input.paymentMethod === 'reimbursement',
    reimbursed_at: null,
    reimbursed_by: null,
    reimbursement_date: null,
    reimbursement_cash_entry_id: null,
    settlement_receipts: [],
    settlement_received_at: null,
    settlement_received_by: null,
    received_receipts: [],
    prepaid_cash_entry_id: null,
    vendor_id: null,
    counterparty: input.counterparty.trim() || null,
    hierarchy_node_id: input.hierarchyNodeId || null,
    invoice_no: null,
    invoice_date: null,
    company_id: input.companyId,
    department_id: input.departmentId,
    account_id: input.accountId,
    sub_account_id: input.subAccountId || '',
    amount: input.amount,
    applied_amount: input.amount,
    posting_date: input.postingDate,
    posting_month: toMonth(input.postingDate),
    payment_date: input.paymentDate,
    payment_month: toMonth(input.paymentDate),
    fiscal_year: fiscalYearOf(input.postingDate, fiscalStartMonth),
    payment_method: input.paymentMethod,
    payment_account_id: input.paymentMethod === 'card' ? (input.paymentAccountId ?? null) : null,
    purchase_date: input.paymentMethod === 'card' ? (input.purchaseDate ?? null) : null,
    cash_entry_id: cashEntryId,
    description: input.description.trim(),
    note: input.note.trim() || null,
    attachments: [],
    receipts,
    // 報告時に実績計上(spec v1.2 §3-D-1)
    paid_at: serverTimestamp(),
    paid_by: userId,
    report_accounting_confirmed_at: null,
    report_accounting_confirmed_by: null,
  });
  await batch.commit();

  // 経理へ確認の依頼(2026-08-27: 専用の「経理確認」ページへの操作依頼。バッジ+お知らせに表示)。
  // 報告は保存済みのため、通知だけ失敗しても失敗とは出さない(2026-09-17 改修依頼 B)
  const { warning } = await runFollowUps([
    [
      '経理への通知',
      async () =>
        notifyUsers({
          userIds: await usersForNotification('accounting'),
          transactionId: id,
          message: `${code} の経理確認をお願いします(支払い済み申請)`,
          kind: 'action',
          stage: 'acct_confirm',
        }),
    ],
  ]);
  return { code, warning };
}

/** 差戻しからの再申請(承認は最初からやり直し)。経費申請のみ(請求書申請は廃止) */
export async function resubmitApplication(params: {
  transaction: Transaction;
  patch: {
    departmentId: DepartmentId;
    hierarchyNodeId: string;
    counterparty: string;
    accountId: string;
    subAccountId: string;
    postingDate: DateStr;
    paymentDate: DateStr;
    amount: number;
    payer: Exclude<Payer, null>;
    paymentMethod: PaymentMethod;
    description: string;
    note: string;
  };
  addedAttachments: StoredFile[];
  applicant: User;
  fiscalStartMonth: number;
}): Promise<{ warning: string | null }> {
  const { transaction, patch, addedAttachments, applicant, fiscalStartMonth } = params;
  const userId = applicant.id;
  if (!isValidAmount(patch.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!patch.paymentDate) throw new Error('支払予定日を入力してください');
  if (!patch.paymentMethod) throw new Error('支払い方法を選んでください');
  if (!patch.description.trim()) throw new Error('摘要は必須です');
  if (
    patch.paymentMethod === 'bank_transfer' &&
    (transaction.attachments ?? []).length + addedAttachments.length === 0
  ) {
    throw new Error('「振込」は請求書の添付が必須です');
  }
  // 独立した取得は同時に行う(2026-09-30: 送信が遅い件)
  const [patterns, users] = await Promise.all([fetchFlowPatterns(), fetchUsersFromServer()]);
  if (!findPattern(patterns, roleOf(applicant), patch.paymentMethod, patch.payer)) {
    throw new Error('この支払方法と支払い者の組合せは選べません(申請フロー仕様§2)');
  }
  const startStatus = resolveStartStatus(users, applicant, patch.departmentId);
  await updateDoc(doc(getDb(), COLLECTION, transaction.id), {
    status: startStatus,
    advance: patch.paymentMethod === 'reimbursement',
    reimbursed_at: null,
    reimbursed_by: null,
    reimbursement_date: null,
    reimbursement_cash_entry_id: null,
    department_id: patch.departmentId,
    hierarchy_node_id: patch.hierarchyNodeId || null,
    counterparty: patch.counterparty.trim() || null,
    vendor_id: null,
    account_id: patch.accountId,
    sub_account_id: patch.subAccountId,
    amount: patch.amount,
    applied_amount: patch.amount,
    payer: patch.payer,
    posting_date: patch.postingDate,
    posting_month: toMonth(patch.postingDate),
    payment_date: patch.paymentDate,
    payment_month: toMonth(patch.paymentDate),
    fiscal_year: fiscalYearOf(patch.postingDate, fiscalStartMonth),
    payment_method: patch.paymentMethod,
    description: patch.description.trim(),
    note: patch.note.trim() || null,
    attachments: [...(transaction.attachments ?? []), ...addedAttachments],
    applied_at: serverTimestamp(),
    applied_by: userId,
    manager_checked_at: startStatus === 'manager_checked' ? serverTimestamp() : null,
    manager_checked_by: startStatus === 'manager_checked' ? userId : null,
    approved_at: startStatus === 'approved' ? serverTimestamp() : null,
    approved_by: startStatus === 'approved' ? userId : null,
    rejected_at: null,
    rejected_by: null,
    reject_reason: null,
    rejected_from_status: null,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });

  // 再申請は保存済み。通知だけ失敗しても失敗とは出さない(2026-09-17 改修依頼 B)
  const code = transaction.code;
  if (!code) return { warning: null };
  return runFollowUps([
    [
      '次の担当者への通知',
      () =>
        notifyNextAfterApply({
          id: transaction.id,
          code,
          departmentId: patch.departmentId,
          startStatus,
          createdBy: userId,
          payer: patch.payer,
          advance: patch.paymentMethod === 'reimbursement',
          paymentMethod: patch.paymentMethod,
          users,
        }),
    ],
  ]);
}

/** 自分の申請一覧(申請のみ・新しい順) */
export async function fetchMyApplications(userId: string, max = 100): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('created_by', '==', userId),
      where('doc_type', 'in', ['expense', 'invoice']),
      orderBy('created_at', 'desc'),
      limit(max),
    ),
  );
  return snap.docs.map(toTransaction);
}

/**
 * 部門長向け: 自分の申請+担当事業部の申請(v3.7)。
 * 部下の申請も申請一覧で確認でき、申請者フィルターで絞れる。
 * Firestoreの 'in' は1クエリ1つまでのため、事業部ごとに等値クエリを発行して結合する
 */
export async function fetchMyAndDeptApplications(
  userId: string,
  departmentIds: DepartmentId[],
  maxPerDept = 100,
): Promise<Transaction[]> {
  const own = await fetchMyApplications(userId);
  const deptSnaps = await Promise.all(
    departmentIds.slice(0, 10).map((d) =>
      getDocs(
        query(
          collection(getDb(), COLLECTION),
          where('department_id', '==', d),
          where('doc_type', 'in', ['expense', 'invoice']),
          orderBy('created_at', 'desc'),
          limit(maxPerDept),
        ),
      ),
    ),
  );
  const merged = new Map<string, Transaction>(own.map((t) => [t.id, t]));
  for (const snap of deptSnaps) for (const d of snap.docs) merged.set(d.id, toTransaction(d));
  return [...merged.values()];
}

/**
 * 繰り返し支払い(家賃・リースなど)の各回。申請一覧に「繰り返し支払い」として出す(2026-09-08 指示)。
 * 承認フローを通らない(doc_type=null)ため fetchAllApplications には含まれない
 */
export async function fetchRecurringForList(max = 300): Promise<Transaction[]> {
  const snap = await getDocs(query(collection(getDb(), COLLECTION), where('source', '==', 'recurring'), limit(max)));
  return snap.docs
    .map(toTransaction)
    .sort((a, b) => (a.payment_date < b.payment_date ? 1 : a.payment_date > b.payment_date ? -1 : 0));
}

/** 全事業部の申請一覧(管理者・経理の進捗確認用・v3.7)。読み取り権限は firestore.rules 側で担保 */
export async function fetchAllApplications(max = 200): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('doc_type', 'in', ['expense', 'invoice']),
      orderBy('created_at', 'desc'),
      limit(max),
    ),
  );
  return snap.docs.map(toTransaction);
}
